import { v4 as uuidv4 } from 'uuid';
import Node from './Node';
import SemanticMemory from '../Memories/SemanticMemory';
import ProceduralMemory from '../Memories/ProceduralMemory';
import { NodeDefinition } from '../../types/models';

class AgentNode extends Node {
  uuid: string; // Immutable UUID field
  semanticMemory: SemanticMemory;
  proceduralMemory: ProceduralMemory;
  type: string;
  childData: AgentNode | null;
  loopElementsKey: string | null;
  dependencies: string[];
  workflowId?: string;
  isEnabled: boolean;
  parentAgentId?: string;
  requiredParameters: string[];
  agentDependencies: string[];
  agent_template_id?: string;

  constructor(
    id: string,
    semanticMemory: SemanticMemory,
    proceduralMemory: ProceduralMemory,
    type: string = 'agent',
    childData: AgentNode | null = null,
    loopElementsKey: string | null = null,
    dependencies: string[] = [],
    parentAgentId?: string,
    requiredParameters: string[] = [],
    agentDependencies: string[] = [],
    agent_template_id?: string,
    uuid?: string // Optional UUID parameter for existing agents
  ) {
    super(id || uuidv4().replace(/-/g, '_')); // Ensure we always have a valid ID
    this.uuid = uuid || uuidv4(); // Generate new UUID if not provided
    this.semanticMemory = semanticMemory;
    this.proceduralMemory = proceduralMemory;
    this.type = type;
    this.childData = childData;
    this.loopElementsKey = loopElementsKey;
    this.dependencies = dependencies;
    this.isEnabled = true;
    this.parentAgentId = parentAgentId;
    this.requiredParameters = requiredParameters;
    this.agentDependencies = agentDependencies;
    this.agent_template_id = agent_template_id;
  }

  toJson(): NodeDefinition {
    const baseJson = {
      _id: this.id,
      uuid: this.uuid, // Include UUID in JSON output
      type: this.type,
      semantic_memory_args: this.type === 'workflow' ? null : this.semanticMemory.toJson(),
      procedural_memory_args: this.proceduralMemory.toJson(),
      working_memory_source: null,
      episodic_memory_source: null,
      depends_on: this.dependencies,
      is_enabled: this.isEnabled,
      required_parameters: this.requiredParameters,
      agent_dependencies: this.agentDependencies,
      ...(this.parentAgentId ? { parent_agent_id: this.parentAgentId } : {}),
      ...(this.type === 'agent_template' && this.agent_template_id ? { agent_template_id: this.agent_template_id } : {})
    };

    if (this.type === 'workflow') {
      return {
        ...baseJson,
        sub_workflow_id: this.workflowId
      };
    }

    if (this.type === 'input_node') {
      return {
        ...baseJson,
        type: 'input_node',
        required_parameters: this.requiredParameters
      };
    }

    if (this.type === 'loop' && this.childData instanceof AgentNode) {
      const childJson = this.childData.toJson();
      return {
        ...baseJson,
        child_data: {
          _id: childJson._id,
          uuid: childJson.uuid, // Include child UUID
          type: 'agent',
          semantic_memory_args: this.childData.semanticMemory.toJson(),
          procedural_memory_args: {
            ...childJson.procedural_memory_args,
            steps: childJson.procedural_memory_args.steps.map(step => ({
              type: step.type,
              params: {
                id: step.params.id,
                description: step.params.description,
                definition: {
                  ...step.params.definition,
                  ...(step.params.definition.condition ? { condition: step.params.definition.condition } : {})
                }
              }
            }))
          },
          working_memory_source: null,
          episodic_memory_source: null,
          depends_on: []
        },
        loop_elements_key: this.loopElementsKey
      };
    }

    return baseJson;
  }

  static fromJson(json: any): AgentNode {
    const semanticMemory = SemanticMemory.fromJson(json.semantic_memory_args);
    const proceduralMemory = ProceduralMemory.fromJson(json.procedural_memory_args);
    
    // Ensure we always have a valid ID
    const agentId = json._id && typeof json._id === 'string' && json._id.trim() !== '' 
      ? json._id 
      : uuidv4().replace(/-/g, '_');
    
    let childData = null;
    if (json.type === 'loop' && json.child_data) {
      const childProceduralMemory = ProceduralMemory.fromJson(json.child_data.procedural_memory_args);
      const childSemanticMemory = SemanticMemory.fromJson(json.child_data.semantic_memory_args);
      
      // Ensure child also has a valid ID
      const childId = json.child_data._id && typeof json.child_data._id === 'string' && json.child_data._id.trim() !== ''
        ? json.child_data._id
        : uuidv4().replace(/-/g, '_');
      
      childData = new AgentNode(
        childId,
        childSemanticMemory,
        childProceduralMemory,
        'agent',
        null,
        null,
        json.child_data.depends_on || [],
        undefined,
        [],
        [],
        undefined,
        json.child_data.uuid // Preserve child UUID if available
      );
    }
    
    const agent = new AgentNode(
      agentId,
      semanticMemory,
      proceduralMemory,
      json.type || 'agent',
      childData,
      json.loop_elements_key || null,
      json.depends_on || [],
      json.parent_agent_id,
      json.required_parameters || [],
      json.agent_dependencies || [],
      json.agent_template_id,
      json.uuid // Preserve existing UUID from JSON
    );

    if (json.type === 'workflow') {
      agent.workflowId = json.sub_workflow_id || json.workflow_id;
    }

    agent.isEnabled = json.is_enabled ?? true;

    return agent;
  }
}

export default AgentNode;